import { addMinutes, format, isSameYear } from 'date-fns';

import type { PlaceSearchRowSlotKind } from '@/components/PlaceSearchAutocomplete/PlaceSearchRowTypeIcon';
import type {
  ItineraryImportDraft,
  ItineraryImportDraftDay,
  ItineraryImportDraftPlace,
  ItineraryImportGtCategory,
  ItineraryImportLocation,
  TripImportResultType,
} from '@/models/itineraryImportTypes';
import type {
  SocialImportReviewPlace,
  SocialImportReviewSection,
} from '@/components/SocialImport/SocialImportPlaceReview';
import {
  extractPhotosFromLocation,
  resolveSampleImageDisplayUrl,
  type LocationDataWithPhotos,
  type PhotoIdentifier,
} from '@/utils/photoUtils';

function matchConfidenceRank(confidence: ItineraryImportDraftPlace['match_confidence']): number {
  if (confidence === 'high') return 0;
  if (confidence === 'low') return 1;
  return 2;
}

/** Drop empty, malformed, or non-string photo URLs (avoids broken proxy / `[object Object]` in review). */
export function coerceImportReviewPhotoUrl(url: string): string | null {
  if (typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  if (
    lower.includes('[object object]') ||
    lower.includes('%5bobject+object%5d') ||
    lower.includes('photo_reference=%5bobject')
  ) {
    return null;
  }
  return trimmed;
}

export function formatImportPlaceAddress(address: unknown): string {
  if (typeof address === 'string') return address.trim();
  if (address && typeof address === 'object') {
    const record = address as Record<string, unknown>;
    for (const key of ['full', 'short', 'a3_intl_address'] as const) {
      const value = record[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
  }
  return '';
}

/** Resolve display URLs for an import draft location (strings, identifiers, proxy URLs). */
export function photoUrlsFromImportPlace(place: ItineraryImportDraftPlace): string[] {
  const loc = place.location;
  if (!loc) return [];

  const urls: string[] = [];
  const seen = new Set<string>();

  const push = (url: string) => {
    const coerced = coerceImportReviewPhotoUrl(url);
    if (!coerced || seen.has(coerced)) return;
    seen.add(coerced);
    urls.push(coerced);
  };

  const rawPhotos = loc.photos as unknown;
  if (Array.isArray(rawPhotos)) {
    for (const item of rawPhotos) {
      if (typeof item === 'string') {
        if (item.trim() === '[object Object]') continue;
        push(resolveSampleImageDisplayUrl(item));
        continue;
      }
      if (item && typeof item === 'object') {
        push(resolveSampleImageDisplayUrl(item as PhotoIdentifier));
      }
    }
  }

  const locForChain = loc as ItineraryImportLocation & LocationDataWithPhotos;
  for (const url of extractPhotosFromLocation(locForChain)) {
    push(url);
  }

  return urls;
}

export interface ImportDraftHeroImage {
  primary: string | null;
  fallbacks: string[];
}

/** Cover image for import review — prefers matched places, then chains fallbacks like post hero logic. */
export function heroImageFromDraft(draft: ItineraryImportDraft): ImportDraftHeroImage {
  const places = draft.days
    .flatMap((day) => day.places)
    .slice()
    .sort((a, b) => matchConfidenceRank(a.match_confidence) - matchConfidenceRank(b.match_confidence));

  const allUrls: string[] = [];
  const seen = new Set<string>();

  for (const place of places) {
    for (const url of photoUrlsFromImportPlace(place)) {
      if (!seen.has(url)) {
        seen.add(url);
        allUrls.push(url);
      }
    }
  }

  if (allUrls.length === 0) {
    const socialDisplay = draft.social?.display_url;
    if (typeof socialDisplay === 'string') {
      const coerced = coerceImportReviewPhotoUrl(socialDisplay);
      if (coerced) {
        return { primary: coerced, fallbacks: [] };
      }
    }
  }

  return {
    primary: allUrls[0] ?? null,
    fallbacks: allUrls.slice(1),
  };
}

function categoryToIconKind(category?: ItineraryImportGtCategory | null): PlaceSearchRowSlotKind {
  if (category === 'stay') return 'stay';
  if (category === 'eat') return 'eat';
  return 'do';
}

function categoryLabel(category?: ItineraryImportGtCategory | null): string {
  if (category === 'stay') return 'Hotel';
  if (category === 'eat') return 'Food';
  if (category === 'do') return 'Experience';
  return 'Place';
}

function isImportPlaceBookable(place: ItineraryImportDraftPlace): boolean {
  if (place.row_kind === 'travel' || !place.location) return false;
  const cat = place.category ?? place.location.gt_category;
  return cat === 'stay' || cat === 'do' || place.location.gt_category === 'stay' || place.location.gt_category === 'do';
}

/** Green chip on import review rows — creators/advisors see earnings copy. */
export function importBookableChipLabel(showEarningsCopy: boolean): string {
  return showEarningsCopy ? 'Bookable · you earn' : 'Bookable';
}

function buildStatusLine(place: ItineraryImportDraftPlace): string | null {
  if (place.row_kind === 'travel') {
    return 'Travel · not a place';
  }
  const tags: string[] = [];
  const cat = place.category ?? place.location?.gt_category;
  tags.push(categoryLabel(cat));
  if (isImportPlaceBookable(place)) {
    tags.push('Bookable');
  }
  if (place.match_confidence === 'low') tags.push('Verify match');
  if (place.match_confidence === 'none') tags.push('Custom stop');
  return tags.length > 0 ? tags.join(' · ') : null;
}

/** Meta line under the trip title on import review (Figma import-docs modal). */
export function formatTripImportReviewMetaLine(
  sourceLabel: string,
  draft: Pick<ItineraryImportDraft, 'start_date_string' | 'end_date_string' | 'day_count'>,
): string {
  const parts: string[] = [sourceLabel];
  const range = formatTripImportDateRange(draft.start_date_string, draft.end_date_string);
  if (range) parts.push(range);
  if (draft.day_count > 0) {
    parts.push(`${draft.day_count} ${draft.day_count === 1 ? 'day' : 'days'}`);
  }
  return parts.join(' · ');
}

export function formatTripImportDateRange(
  startDateString?: string | null,
  endDateString?: string | null,
): string | null {
  if (!startDateString?.trim()) return null;
  const timeZoneOffsetMinutes = new Date('2024-01-01').getTimezoneOffset();
  const start = addMinutes(new Date(startDateString), timeZoneOffsetMinutes);
  if (Number.isNaN(start.getTime())) return null;
  if (!endDateString?.trim()) {
    return format(start, 'MMM d, yyyy');
  }
  const end = addMinutes(new Date(endDateString), timeZoneOffsetMinutes);
  if (Number.isNaN(end.getTime())) return format(start, 'MMM d, yyyy');
  const startFormat = isSameYear(start, end) ? 'MMM d' : 'MMM d, yyyy';
  return `${format(start, startFormat)} - ${format(end, 'MMM d, yyyy')}`;
}

/** Success modal trip card, e.g. `Trip · May 25 – 31, 2025`. */
export function formatTripImportTripDateSubtitle(
  draft: Pick<ItineraryImportDraft, 'start_date_string' | 'end_date_string'>,
): string | null {
  const startDateString = draft.start_date_string?.trim();
  const endDateString = draft.end_date_string?.trim();
  if (!startDateString) return null;

  const timeZoneOffsetMinutes = new Date('2024-01-01').getTimezoneOffset();
  const start = addMinutes(new Date(startDateString), timeZoneOffsetMinutes);
  if (Number.isNaN(start.getTime())) return null;

  if (!endDateString) {
    return `Trip · ${format(start, 'MMM d, yyyy')}`;
  }

  const end = addMinutes(new Date(endDateString), timeZoneOffsetMinutes);
  if (Number.isNaN(end.getTime())) {
    return `Trip · ${format(start, 'MMM d, yyyy')}`;
  }

  let range: string;
  if (isSameYear(start, end) && format(start, 'MMM') === format(end, 'MMM')) {
    range = `${format(start, 'MMM d')} – ${format(end, 'd, yyyy')}`;
  } else if (isSameYear(start, end)) {
    range = `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`;
  } else {
    range = `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
  }

  return `Trip · ${range}`;
}

function formatImportDayDateLabel(date: string): string | null {
  const trimmed = date.trim();
  if (!trimmed) return null;
  const normalized = trimmed.includes('T') ? trimmed : `${trimmed}T12:00:00`;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return trimmed;
  return format(parsed, 'EEE, MMM d');
}

function daySectionHeadings(day: ItineraryImportDraftDay): {
  heading: string;
  subheading: string | null;
} {
  const heading = `Day ${day.day_index + 1}`;
  const dateLabel = day.date?.trim() ? formatImportDayDateLabel(day.date) : null;
  if (dateLabel) {
    return { heading, subheading: dateLabel };
  }
  const label = day.label?.trim();
  if (!label) {
    return { heading, subheading: null };
  }
  const dayPrefix = new RegExp(`^Day\\s+${day.day_index + 1}\\s*[—–-]?\\s*`, 'i');
  const remainder = label.replace(dayPrefix, '').trim();
  if (remainder && remainder !== label) {
    return { heading, subheading: remainder };
  }
  if (/^Day\s+\d+/i.test(label)) {
    return { heading, subheading: label.replace(/^Day\s+\d+\s*[—–-]?\s*/i, '').trim() || null };
  }
  return { heading, subheading: label };
}

export function mapDraftPlaceToReviewPlace(
  place: ItineraryImportDraftPlace,
  resultType: TripImportResultType,
  options?: {
    showDetailed?: boolean;
    showBookableEarnings?: boolean;
    /** Notes edit in expanded row; collapsed subtitle prefers address. */
    notesInExpandedPanel?: boolean;
  },
): SocialImportReviewPlace {
  const loc = place.location;
  const title = place.name.trim() || loc?.name?.trim() || 'Place';
  const address = formatImportPlaceAddress(loc?.address);
  const imageUrls = photoUrlsFromImportPlace(place);
  const cat = place.category ?? loc?.gt_category;
  const sourceQuote = place.source_quote?.trim() || null;
  const matchReason =
    place.match_confidence === 'low' ? place.match_reason?.trim() || null : null;

  const noteText = place.notes?.trim() || '';
  const subtitle =
    options?.showDetailed && noteText
      ? noteText
      : options?.notesInExpandedPanel
        ? address
        : address || noteText;

  const isTravel = place.row_kind === 'travel';

  return {
    id: place.client_id,
    title,
    subtitle,
    imageUrl: imageUrls[0] ?? null,
    iconKind: categoryToIconKind(cat),
    disabled: isTravel,
    statusLine: buildStatusLine(place),
    bookableChip: isImportPlaceBookable(place)
      ? importBookableChipLabel(Boolean(options?.showBookableEarnings))
      : null,
    isTravel,
    matchConfidence: place.match_confidence,
    matchReason,
    sourceQuote,
    editableNotes: noteText,
    canFindPlace: !isTravel,
    findPlaceLabel: loc ? 'Change' : 'Find this place',
  };
}

export function buildTripImportReviewSections(
  draft: ItineraryImportDraft,
  resultType: TripImportResultType,
  options?: {
    showDetailed?: boolean;
    showBookableEarnings?: boolean;
    notesInExpandedPanel?: boolean;
  },
): SocialImportReviewSection[] {
  if (resultType === 'list') {
    const places = draft.days.flatMap((day) =>
      day.places.map((place) => mapDraftPlaceToReviewPlace(place, resultType, options)),
    );
    return [{ key: 'all', places }];
  }

  return draft.days.map((day) => {
    const { heading, subheading } = daySectionHeadings(day);
    return {
      key: `day-${day.day_index}`,
      heading,
      subheading,
      description: day.notes?.trim() || null,
      places: day.places.map((place) => mapDraftPlaceToReviewPlace(place, resultType, options)),
    };
  });
}

export function countDraftPlacesTotal(draft: ItineraryImportDraft): number {
  return draft.days.reduce((total, day) => total + day.places.length, 0);
}

export function coverImageFromDraft(draft: ItineraryImportDraft): string | null {
  return heroImageFromDraft(draft).primary;
}
